import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, Pencil, LayoutTemplate, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WidgetLibraryTab } from "../components/WidgetLibraryTab";
import WidgetGenerator from "./WidgetGenerator";
import { toast } from "sonner";
import { api, errMsg, ROLE_LABEL } from "../lib/api";
import { useGet } from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { PageHeader, Pill } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { WIDGETS, METRICS, DashboardRenderer, DEFAULT_SUMMARY_CARDS } from "../components/dashboard/Widgets";
import { quoteOfTheDay } from "../data/quotes";
import { AssetPicker } from "../components/dashboard/AssetPicker";
import { ACCOUNT_NAV } from "../components/AppShell";
import { SidebarAccessTab } from "../components/features/SidebarAccess";
import { ReleaseTab } from "../components/features/ReleaseTab";

const ROLES = ["agency_owner", "subaccount_admin", "staff"];
const uid = () => Math.random().toString(36).slice(2, 10);

function Checks({ options, value, onChange, testid }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([k, label]) => (
        <button key={k} type="button" onClick={() => toggle(k)} data-testid={`${testid}-${k.replace(/\//g, "-")}`}
          className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${value.includes(k) ? "border-[#C2A462] bg-[#C2A462]/15 text-[#EBD6A0]" : "text-muted-foreground hover:border-[#C2A462]/50"}`}>{label}</button>
      ))}
    </div>
  );
}

function SummaryCardsConfig({ c, up, tid }) {
  const cards = c.cards || [];
  const setCard = (i, k, v) => up("cards", cards.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const move = (i, dir) => { const n = [...cards]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; up("cards", n); };
  const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";
  return (
    <div className="space-y-3">
      {cards.map((k, i) => (
        <div key={i} className="rounded-md border border-white/10 p-3 space-y-2" data-testid={tid(`card-${i}`)}>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="w-48" value={k.label} onChange={(e) => setCard(i, "label", e.target.value)} data-testid={tid(`card-${i}-label`)} />
            <select className={sel} value={k.metric} onChange={(e) => setCard(i, "metric", e.target.value)} data-testid={tid(`card-${i}-metric`)}>
              {Object.entries(METRICS).map(([m, v]) => <option key={m} value={m}>Counter: {v[0]}</option>)}
            </select>
            <div className="ml-auto flex gap-1">
              <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" disabled={i === cards.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => up("cards", cards.filter((_, j) => j !== i))} data-testid={tid(`card-${i}-remove`)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
          <AssetPicker value={k.icon_url} onChange={(v) => setCard(i, "icon_url", v)} testid={tid(`card-${i}-icon`)} />
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => up("cards", [...cards, { metric: "contacts", label: "New card", icon_url: "" }])} data-testid={tid("add-card")}><Plus className="h-3.5 w-3.5 mr-1" />Add card</Button>
        <Button size="sm" variant="ghost" onClick={() => up("cards", DEFAULT_SUMMARY_CARDS)} data-testid={tid("reset-cards")}>Reset to standard 7</Button>
      </div>
      <p className="text-xs text-muted-foreground">Leave the icon empty to use the built-in icon, or choose your own image from the Media Library.</p>
    </div>
  );
}

function LibraryPick({ value, onChange, tid }) {
  const [lib] = useGet("/widget-library");
  return (
    <div className="space-y-1">
      <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid("library-pick")}>
        <option value="">Choose a shared widget…</option>
        {(lib || []).map((x) => <option key={x.id} value={x.id}>{x.category} · {x.name}{x.status === "draft" ? " (draft)" : ""}</option>)}
      </select>
      {lib && !lib.length && <p className="text-xs text-muted-foreground">No widgets have been shared with this organisation's plan yet.</p>}
    </div>
  );
}

function WidgetConfig({ w, set }) {
  const c = w.config || {};
  const up = (k, v) => set({ ...w, config: { ...c, [k]: v } });
  const tid = (k) => `widget-${w.id}-${k}`;
  const dark = (
    <div className="flex items-center gap-3 text-xs">
      <span className="text-muted-foreground w-28">Banner darkness</span>
      <span className="text-muted-foreground">Light</span>
      <input type="range" min={0} max={90} step={5} value={c.overlay ?? 50} onChange={(e) => up("overlay", Number(e.target.value))} className="flex-1 accent-[#C2A462]" data-testid={tid("overlay")} />
      <span className="text-muted-foreground">Deep</span>
      <span className="w-10 text-right font-mono text-[#EBD6A0]" data-testid={tid("overlay-value")}>{c.overlay ?? 50}%</span>
    </div>
  );
  const toggle = (k, label) => (
    <label className="flex items-center gap-2 text-xs"><input type="checkbox" className="accent-[#C2A462]" checked={!!c[k]} onChange={(e) => up(k, e.target.checked)} data-testid={tid(k)} />{label}</label>
  );
  if (w.type === "greeting_banner") return (
    <div className="space-y-3">
      <AssetPicker value={c.image_url} onChange={(v) => up("image_url", v)} testid={tid("image")} />
      <div className="flex gap-5">{toggle("show_name", "Show first name")}{toggle("show_datetime", "Show date & time")}</div>
      {dark}
      <Input placeholder="Subtitle (optional)" value={c.subtitle || ""} onChange={(e) => up("subtitle", e.target.value)} data-testid={tid("subtitle")} />
    </div>
  );
  if (w.type === "page_banner") return (
    <div className="space-y-3">
      <AssetPicker value={c.image_url} onChange={(v) => up("image_url", v)} testid={tid("image")} />
      {dark}
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Overline" value={c.overline || ""} onChange={(e) => up("overline", e.target.value)} data-testid={tid("overline")} />
        <Input placeholder="Title" value={c.title || ""} onChange={(e) => up("title", e.target.value)} data-testid={tid("title")} />
      </div>
    </div>
  );
  if (w.type === "summary_cards") return <SummaryCardsConfig c={c} up={up} tid={tid} />;
  if (w.type === "library") return <LibraryPick value={c.widget_def_id} onChange={(v) => up("widget_def_id", v)} tid={tid} />;
  if (w.type === "daily_quote") return (
    <div className="space-y-2">
      <Input placeholder="Heading" value={c.heading || ""} onChange={(e) => up("heading", e.target.value)} data-testid={tid("heading")} />
      <p className="text-xs text-muted-foreground">Today's message: “{quoteOfTheDay()}” — a different one appears each day of the year.</p>
    </div>
  );
  if (w.type === "stats") return <Checks testid={tid("metric")} options={Object.entries(METRICS).map(([k, v]) => [k, v[0]])} value={c.metrics || []} onChange={(v) => up("metrics", v)} />;
  if (w.type === "quick_links") return <Checks testid={tid("link")} options={ACCOUNT_NAV.map((n) => [n.to, n.label])} value={c.links || []} onChange={(v) => up("links", v)} />;
  if (w.type === "note") return (
    <div className="space-y-2">
      <Input placeholder="Title" value={c.title || ""} onChange={(e) => up("title", e.target.value)} data-testid={tid("title")} />
      <Textarea placeholder="Message" value={c.body || ""} onChange={(e) => up("body", e.target.value)} data-testid={tid("body")} />
    </div>
  );
  return <div className="text-xs text-muted-foreground">No settings. Uses live data from this entity.</div>;
}

function WidgetRow({ w, i, n, set, move, remove }) {
  return (
    <div className="rounded-md border bg-card p-4 space-y-3" data-testid={`widget-row-${w.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-[#EBD6A0] flex-1">{WIDGETS[w.type]?.label}</span>
        <Select value={w.width} onValueChange={(v) => set({ ...w, width: v })}>
          <SelectTrigger className="w-28 h-8" data-testid={`widget-${w.id}-width`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="full">Full width</SelectItem><SelectItem value="half">Half</SelectItem><SelectItem value="third">Third</SelectItem></SelectContent>
        </Select>
        <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`widget-${w.id}-up`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => move(i, 1)} data-testid={`widget-${w.id}-down`}><ArrowDown className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`widget-${w.id}-remove`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
      </div>
      <WidgetConfig w={w} set={set} />
    </div>
  );
}

function Editor({ initial, onSaved, onCancel, lab }) {
  const nav = useNavigate();
  const [d, setD] = useState(initial);
  const [preview, setPreview] = useState(false);
  const [add, setAdd] = useState("");
  const [data] = useGet("/dashboard");
  const [people] = useGet("/users");
  const ws = d.widgets || [];
  const setW = (i, w) => setD({ ...d, widgets: ws.map((x, j) => (j === i ? w : x)) });
  const move = (i, dir) => { const a = [...ws]; [a[i], a[i + dir]] = [a[i + dir], a[i]]; setD({ ...d, widgets: a }); };
  const addWidget = (type) => { setD({ ...d, widgets: [...ws, { id: uid(), type, width: "full", config: { ...WIDGETS[type].defaults } }] }); setAdd(""); };
  const save = async () => {
    try {
      const body = { ...d, positions: (d.positionsText ?? d.positions.join(", ")).split(",").map((s) => s.trim()).filter(Boolean) };
      delete body.positionsText;
      d.id ? await api.put(`/dashboards/${d.id}`, body) : await api.post("/dashboards", body);
      toast.success(d.is_template ? "Template saved" : "Dashboard saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="dashboard-editor">
      <PageHeader overline={lab ? "Features Lab" : "Features Builder"} title={d.id ? d.name || "Dashboard" : d.is_template ? "New template" : "New dashboard"}>
        {d.id && !d.is_template && <Button variant="secondary" onClick={() => nav(`/app/dashboard?view=${d.id}`)} data-testid="view-on-dashboard-btn"><ExternalLink className="h-4 w-4 mr-1" />View on Dashboard</Button>}
        <Button variant="secondary" onClick={() => setPreview(!preview)} data-testid="toggle-preview-btn">{preview ? <><Pencil className="h-4 w-4 mr-1" />Edit</> : <><Eye className="h-4 w-4 mr-1" />Preview</>}</Button>
        <Button variant="ghost" onClick={onCancel} data-testid="cancel-dashboard-btn">Back</Button>
        <Button onClick={save} data-testid="save-dashboard-btn">{d.is_template ? "Save template" : "Save dashboard"}</Button>
      </PageHeader>
      {preview ? <DashboardRenderer dashboard={d} data={data} /> : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="rounded-md border bg-card p-5 space-y-4 h-fit">
            <div><Label>{d.is_template ? "Template name" : "Name"}</Label><Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid="dashboard-name-input" /></div>
            {d.is_template && <p className="text-xs text-muted-foreground" data-testid="template-note">This is a template. Nobody sees it as their dashboard. Pick it when you create a new dashboard to start with these widgets.</p>}
            {!d.is_template && <>
            <div><Label>Show to roles</Label><div className="mt-2"><Checks testid="dashboard-role" options={ROLES.map((r) => [r, ROLE_LABEL[r]])} value={d.roles} onChange={(v) => setD({ ...d, roles: v })} /></div></div>
            <div><Label>Show to positions (comma separated)</Label>
              <Input placeholder="e.g. Team Leader, Practice Suite" value={d.positionsText ?? d.positions.join(", ")} onChange={(e) => setD({ ...d, positionsText: e.target.value })} data-testid="dashboard-positions-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Matches the Position set on each user (Settings → Users). Position beats role.</p></div>
            <div><Label>Show to specific people (beats position and role)</Label><div className="mt-2"><Checks testid="dashboard-person" options={(people || []).filter((u) => !u.disabled).map((u) => [u.id, u.name])} value={d.user_ids || []} onChange={(v) => setD({ ...d, user_ids: v })} /></div>
              <p className="text-[11px] text-muted-foreground mt-1">Use this when someone (e.g. a CEO) isn't an admin.</p></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C2A462]" checked={d.is_default} onChange={(e) => setD({ ...d, is_default: e.target.checked })} data-testid="dashboard-default-toggle" />Default for everyone else</label>
            </>}
          </div>
          <div className="xl:col-span-2 space-y-3">
            {ws.map((w, i) => <WidgetRow key={w.id} w={w} i={i} n={ws.length} set={(x) => setW(i, x)} move={move} remove={(j) => setD({ ...d, widgets: ws.filter((_, k) => k !== j) })} />)}
            <Select value={add} onValueChange={addWidget}>
              <SelectTrigger className="w-72" data-testid="add-widget-select"><SelectValue placeholder="+ Add widget" /></SelectTrigger>
              <SelectContent>{Object.entries(WIDGETS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`add-widget-${k}`}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}

function NewDashboardDialog({ templates, onPick, onClose }) {
  const [tpl, setTpl] = useState("");
  const start = () => {
    const t = templates.find((x) => x.id === tpl);
    onPick({ name: "", roles: [], positions: [], is_default: false, is_template: false,
      widgets: t ? t.widgets.map((w) => ({ ...w, id: uid(), config: JSON.parse(JSON.stringify(w.config || {})) })) : [] });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-dashboard-dialog">
        <DialogHeader><DialogTitle>New dashboard</DialogTitle><DialogDescription>Start from scratch or from one of your templates.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <label className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm ${!tpl ? "border-[#C2A462] bg-[#C2A462]/10" : "border-input"}`} data-testid="new-from-scratch"><input type="radio" className="accent-[#C2A462]" checked={!tpl} onChange={() => setTpl("")} />Start from scratch (empty)</label>
          {templates.map((t) => <label key={t.id} className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm ${tpl === t.id ? "border-[#C2A462] bg-[#C2A462]/10" : "border-input"}`} data-testid={`new-from-template-${t.id}`}><input type="radio" className="accent-[#C2A462]" checked={tpl === t.id} onChange={() => setTpl(t.id)} /><span className="flex-1">{t.name || "Untitled template"}</span><span className="text-xs text-muted-foreground">{t.widgets.length} widgets</span></label>)}
          {!templates.length && <p className="text-xs text-muted-foreground">No templates yet. Use New template to create one.</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={start} data-testid="new-dashboard-start">Create dashboard</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function DashCard({ d, lab, onEdit, onRemove, onView }) {
  return (
    <div className="rounded-md border bg-card p-5" data-testid={`dashboard-card-${d.id}`}>
      <div className="flex items-start gap-3">
        <LayoutTemplate className="h-5 w-5 text-[#C2A462] mt-0.5" strokeWidth={1.6} />
        <div className="flex-1"><div className="font-medium">{d.name || "Untitled"}</div><div className="text-xs text-muted-foreground">{d.widgets.length} widgets</div></div>
        {d.is_template && <Pill tone="amber">Template</Pill>}
        {d.is_default && <Pill tone="teal">Default</Pill>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {d.released_from && !lab && <Pill tone="blue">From Dev Lab</Pill>}
        {d.roles.map((r) => <Pill key={r}>{ROLE_LABEL[r]}</Pill>)}
        {d.positions.map((p) => <Pill key={p} tone="blue">{p}</Pill>)}
        {!!d.user_ids?.length && <Pill tone="amber">{d.user_ids.length} {d.user_ids.length === 1 ? "person" : "people"}</Pill>}
        {!lab && !d.is_template && !d.roles.length && !d.positions.length && !d.user_ids?.length && !d.is_default && <Pill tone="red">Not assigned yet — nobody sees this</Pill>}
      </div>
      <div className="mt-4 flex gap-2">
        <Button size="sm" variant="secondary" onClick={onEdit} data-testid={`edit-dashboard-${d.id}`}>{lab || d.is_template ? "Edit" : "Assign & edit"}</Button>
        <Button size="sm" variant="outline" onClick={onView} data-testid={`view-dashboard-${d.id}`}><Eye className="h-3.5 w-3.5 mr-1" />View</Button>
        <Button size="sm" variant="ghost" onClick={onRemove} data-testid={`delete-dashboard-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
      </div>
    </div>
  );
}

export default function DashboardBuilder({ embedded = false }) {
  const [list, reload] = useGet("/dashboards");
  const [edit, setEdit] = useState(null);
  const [picking, setPicking] = useState(false);
  const navTo = useNavigate();
  const { user, account } = useAuth();
  const owner = user.role === "agency_owner";
  const lab = owner && account?.is_lab;
  if (edit) return <Editor lab={lab} initial={edit} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />;
  const remove = async (d) => { if (!window.confirm(`Delete this ${d.is_template ? "template" : "dashboard"}?`)) return; try { await api.delete(`/dashboards/${d.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const templates = (list || []).filter((d) => d.is_template), boards = (list || []).filter((d) => !d.is_template);
  const card = (d) => <DashCard key={d.id} d={d} lab={lab} onEdit={() => setEdit(d)} onRemove={() => remove(d)} onView={() => navTo(`/app/dashboard?view=${d.id}`)} />;
  return (
    <div data-testid="dashboard-builder-page">
      {!(embedded && !lab) && <PageHeader overline="Workspace" title={lab ? "Features Lab" : "Features Builder"} desc={lab ? "Design each dashboard visually, request new widgets, then release finished dashboards and sidebar sections to Entity Accounts." : "Decide who gets each released dashboard and sidebar section in your organisation."}>
        {lab && <Button variant="secondary" onClick={() => setEdit({ name: "", roles: [], positions: [], is_default: false, is_template: true, widgets: [] })} data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" />New template</Button>}
        {lab && <Button onClick={() => setPicking(true)} data-testid="new-dashboard-btn"><Plus className="h-4 w-4 mr-1" />New dashboard</Button>}
      </PageHeader>}
      {picking && <NewDashboardDialog templates={templates} onClose={() => setPicking(false)} onPick={(d) => { setPicking(false); setEdit(d); }} />}
      <Tabs defaultValue="dashboards">
        <TabsList className="mb-5">
          <TabsTrigger value="dashboards" data-testid="tab-dashboards">Dashboards</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-widget-library">Widget Library</TabsTrigger>
          {lab && <TabsTrigger value="generator" data-testid="tab-widget-creator">Widget Creator</TabsTrigger>}
          {lab && <TabsTrigger value="release" data-testid="tab-release">Release</TabsTrigger>}
          {!lab && <TabsTrigger value="sidebar" data-testid="tab-sidebar-access">Sidebar access</TabsTrigger>}
        </TabsList>
        <TabsContent value="library"><WidgetLibraryTab dashboards={list} onAdded={reload} lab={lab} /></TabsContent>
        {lab && <TabsContent value="generator"><WidgetGenerator embedded /></TabsContent>}
        {lab && <TabsContent value="release"><ReleaseTab dashboards={boards} /></TabsContent>}
        {!lab && <TabsContent value="sidebar"><SidebarAccessTab /></TabsContent>}
        <TabsContent value="dashboards">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {boards.map(card)}
        {list && !boards.length && <div className="text-sm text-muted-foreground" data-testid="no-dashboards">{lab ? "No dashboards yet. Click New dashboard to design one." : "No dashboards have been released to your organisation yet."}</div>}
      </div>
      {(lab || templates.length > 0) && <div className="mt-8" data-testid="templates-section">
        <div className="overline mb-3">Templates</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{templates.map(card)}</div>
        {!templates.length && <p className="text-sm text-muted-foreground">No templates yet. Click New template to create one with your default widgets.</p>}
      </div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
